// 🌿 API Craft Back Office - Auth Context
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthResponse } from '../types';
import { authApi } from '../services/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const profile = await authApi.getProfile();
          
          // Map API response to expected User interface
          const user: User = {
            id: profile.user_id,
            email: profile.email,
            email_confirmed_at: profile.email_confirmed_at,
            created_at: profile.created_at,
            last_sign_in: profile.last_sign_in
          };
          

          setUser(user);
        } catch (error) {

          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
        }
      } else {

      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response: AuthResponse = await authApi.login({ email, password });
      
      // Store tokens securely
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Set user state (response.user is already in correct format for login)
      const user: User = response.user;
      

      setUser(user);
      setIsLoading(false);
      
      // Force immediate redirect after successful login
      window.location.href = '/';
    } catch (error) {

      setIsLoading(false);
      throw error;
    }
  };

  const register = async (email: string, password: string, confirmPassword: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response: AuthResponse = await authApi.register({ email, password, confirm_password: confirmPassword });
      
      // Store tokens securely
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      
      // Set user state
      setUser(response.user);
      
      console.log('✅ Registration successful for:', response.user.email);
    } catch (error) {
      console.error('🚨 Registration failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await authApi.logout();
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('🚨 Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      setIsLoading(false);
    }
  };

  const refreshProfile = async (): Promise<void> => {
    try {
      const profile = await authApi.getProfile();
      
      // Map API response to expected User interface
      const user: User = {
        id: profile.user_id,
        email: profile.email,
        email_confirmed_at: profile.email_confirmed_at,
        created_at: profile.created_at,
        last_sign_in: profile.last_sign_in
      };
      
      setUser(user);

    } catch (error) {

      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
